import { NavLink, useNavigate } from "react-router-dom";
import type { ChatSession } from "@/hooks/useChatManager";
import { useAuth } from "@/auth/AuthContext";
import {
  IconChat,
  IconWorkflow,
  IconReport,
  IconTerminal,
  IconPlus,
  IconX,
  IconPanel,
  IconMemory,
  IconOrg,
  IconEmail,
  IconGateway,
  IconWhatsApp,
} from "@/components/Icons";
import styles from "./Sidebar.module.css";

const IS_TEST_MODE =
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "1" ||
  String(import.meta.env.VITE_UI_ONLY_MODE ?? "").toLowerCase() === "true";

interface Props {
  workflowCount: number;
  reportCount: number;
  devOpen: boolean;
  onToggleDev: () => void;
  memoryPanelOpen: boolean;
  onToggleMemoryPanel: () => void;
  sessions: ChatSession[];
  activeChatId: string;
  onNewChat: () => void;
  onSwitchChat: (id: string) => void;
  onDeleteChat: (id: string) => void;
  onEndSession: (id: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onReportIssue: () => void;
  chatsLoaded?: boolean;
}

export default function Sidebar({
  workflowCount,
  reportCount,
  devOpen,
  onToggleDev,
  memoryPanelOpen,
  onToggleMemoryPanel,
  sessions,
  activeChatId,
  onNewChat,
  onSwitchChat,
  onDeleteChat,
  onEndSession,
  collapsed,
  onToggleCollapse,
  onReportIssue,
  chatsLoaded = true,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Derive user initials for the avatar
  const initials = user
    ? (user.firstName?.[0] ?? user.email[0]).toUpperCase()
    : "?";

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
      {/* Brand */}
      <div className={styles.brand}>
        <img
          src="/foreman-logo-new.png"
          width={42}
          height={42}
          alt="Foreman"
          className={styles.logoImg}
          onClick={collapsed ? onToggleCollapse : undefined}
          style={collapsed ? { cursor: "pointer" } : undefined}
        />
        {!collapsed && <span className={styles.logoText}>Foreman</span>}
        <button
          className={styles.collapseBtn}
          onClick={onToggleCollapse}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-attr="sidebar-collapse-toggle"
        >
          <IconPanel width={16} height={16} />
        </button>
      </div>

      {/* New Chat */}
      <button
        className={styles.newChat}
        title="New conversation"
        data-attr="sidebar-new-chat"
        onClick={() => {
          onNewChat();
          navigate("/app/chat");
        }}
      >
        <IconPlus width={16} height={16} />
        {!collapsed && <span>New Chat</span>}
      </button>

      {/* Top nav items (like ChatGPT's Images, Apps, etc.) */}
      <nav className={styles.topNav}>
        <NavLink
          to="/app/workflows"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Actions"
          data-attr="sidebar-nav-workflows"
        >
          <IconWorkflow width={18} height={18} />
          {!collapsed && <span>Actions</span>}
          {!collapsed && workflowCount > 0 && (
            <span className={styles.badge}>{workflowCount}</span>
          )}
        </NavLink>

        <NavLink
          to="/app/reports"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Reports"
          data-attr="sidebar-nav-reports"
        >
          <IconReport width={18} height={18} />
          {!collapsed && <span>Reports</span>}
          {!collapsed && reportCount > 0 && (
            <span className={styles.badge}>{reportCount}</span>
          )}
        </NavLink>

        <NavLink
          to="/app/organisation"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Organisation"
          data-attr="sidebar-nav-organisation"
        >
          <IconOrg width={16} height={16} />
          {!collapsed && <span>Organisation</span>}
        </NavLink>

        <NavLink
          to="/app/email"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Email"
          data-attr="sidebar-nav-email"
        >
          <IconEmail width={18} height={18} />
          {!collapsed && <span>Email</span>}
        </NavLink>

        <NavLink
          to="/app/whatsapp"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="WhatsApp"
          data-attr="sidebar-nav-whatsapp"
        >
          <IconWhatsApp width={18} height={18} />
          {!collapsed && <span>WhatsApp</span>}
        </NavLink>

        <NavLink
          to="/app/crm"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="CRM"
          data-attr="sidebar-nav-crm"
        >
          <IconGateway width={18} height={18} />
          {!collapsed && <span>CRM</span>}
        </NavLink>

        <NavLink
          to="/app/memory"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Memory"
          data-attr="sidebar-nav-memory"
        >
          <IconMemory width={18} height={18} />
          {!collapsed && <span>Memory</span>}
        </NavLink>
      </nav>

      <div className={styles.divider} />

      {/* Chat history */}
      {!collapsed && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Your chats</div>
          <div className={styles.chatList}>
            {!chatsLoaded
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className={styles.skeleton} />
                ))
              : sessions.map((s) => (
                  <button
                    key={s.id}
                    className={`${styles.chatItem} ${s.id === activeChatId ? styles.active : ""}`}
                    data-attr="sidebar-chat-item"
                    onClick={() => {
                      onSwitchChat(s.id);
                      navigate("/app/chat");
                    }}
                  >
                    <IconChat width={14} height={14} />
                    <span className={styles.chatItemText}>{s.title}</span>
                    <span
                      className={styles.endSessionBtn}
                      data-attr="sidebar-end-session"
                      aria-label="End session"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEndSession(s.id);
                      }}
                      title="End session (flush memory)"
                    >
                      ⏹
                    </span>
                    {sessions.length > 1 && (
                      <span
                        className={styles.chatDeleteBtn}
                        data-attr="sidebar-delete-chat"
                        aria-label="Delete chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteChat(s.id);
                        }}
                        title="Delete chat"
                      >
                        <IconX width={12} height={12} />
                      </span>
                    )}
                  </button>
                ))}
          </div>
        </div>
      )}

      {/* Spacer */}
      <div className={styles.spacer} />

      {/* Bottom nav */}
      <nav className={styles.nav}>
        <button
          className={`${styles.navItem} ${devOpen ? styles.active : ""}`}
          onClick={onToggleDev}
          title="Dev Panel"
          data-attr="sidebar-dev-panel"
          style={IS_TEST_MODE ? undefined : { display: "none" }}
        >
          <IconTerminal width={16} height={16} />
          {!collapsed && <span>Dev Panel</span>}
        </button>

        <button
          className={`${styles.navItem} ${memoryPanelOpen ? styles.active : ""}`}
          onClick={onToggleMemoryPanel}
          title="Memory Panel"
          data-attr="sidebar-memory-panel"
          style={IS_TEST_MODE ? undefined : { display: "none" }}
        >
          <IconMemory width={16} height={16} />
          {!collapsed && <span>Memory</span>}
        </button>

        <NavLink
          to="/app/gateway"
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ""}`
          }
          title="Gateway"
          data-attr="sidebar-nav-gateway"
          style={IS_TEST_MODE ? undefined : { display: "none" }}
        >
          <IconGateway width={16} height={16} />
          {!collapsed && <span>Gateway</span>}
        </NavLink>
      </nav>

      {/* Footer: status + user */}
      <div className={styles.footer}>
        <button
          className={styles.navItem}
          onClick={onReportIssue}
          title="Report an issue"
          aria-label="Report Issue"
          data-attr="sidebar-report-issue"
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {!collapsed && <span>Report Issue</span>}
        </button>

        <div className={styles.status}>
          <div className={styles.statusDot} />
          {!collapsed && <span className={styles.statusLabel}>Connected</span>}
        </div>

        <NavLink
          to="/app/profile"
          className={({ isActive }) =>
            `${styles.userBtn} ${isActive ? styles.active : ""}`
          }
          title="Account settings"
          data-attr="sidebar-account"
        >
          <span className={styles.avatar}>{initials}</span>
          {!collapsed && (
            <span className={styles.userName}>
              {user?.firstName || user?.email || "Account"}
            </span>
          )}
        </NavLink>
      </div>
    </aside>
  );
}
